import { InfoCircleOutlined, MailOutlined, PhoneOutlined } from "@ant-design/icons";
import { Alert, Card, Checkbox, Input, Modal, Tag, Tooltip, Typography } from "antd";
import FullHeightContainerLayout from "components/atoms/FullHeightContainerLayout";
import { useCallback, useMemo, useState } from "react";
import CypressTestIds from "../../../cypress/CypressTestIds";

const { CheckableTag } = Tag;
const { Text } = Typography;
const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const phoneRegex = /^\+?[0-9()\-\s]{6,}$/;

const isEmailField = (field) => field.dataType === "email" || field.name.toLowerCase().includes("email") || (field.classifiers ?? []).some((tag) => tag.tagName === "email");

const isPhoneField = (field) => field.dataType === "phone" || field.name.toLowerCase().includes("phone") || (field.classifiers ?? []).some((tag) => tag.tagName === "phone");

const isContactField = (field) => isEmailField(field) || isPhoneField(field);

/** @type {import('./types').IDataObjectRequestVerificationModal} */
const DataObjectRequestVerificationModal = ({ recordId, visible, onCancel, onOk, schemaFields, dataObject, busy, showConfirm }) => {
    const getFieldStatus = useCallback(
        (field) => {
            const rawValue = dataObject?.[field.fieldId];
            const normalized = rawValue === undefined || rawValue === null ? "" : String(rawValue).trim();
            const hasValue = normalized.length > 0;

            const contactType = isEmailField(field) ? "email" : isPhoneField(field) ? "phone" : null;
            const isValidContact = contactType === "email" ? emailRegex.test(normalized) : contactType === "phone" ? phoneRegex.test(normalized) : false;

            const disabled = contactType && (!hasValue || !isValidContact);

            const tooltip = contactType
                ? !hasValue
                    ? "This attribute is empty in the consumer record."
                    : !isValidContact
                      ? contactType === "email"
                          ? "Provide a valid email value for this record."
                          : "Provide a valid phone value for this record."
                      : `This ${contactType} will receive the verification link.`
                : "Optional attribute. Include it if you need it reviewed.";

            return {
                contactType,
                isValidContact,
                disabled,
                tooltip,
            };
        },
        [dataObject],
    );

    const preselectedContacts = useMemo(
        () =>
            schemaFields
                .filter((field) => isContactField(field))
                .filter((field) => !getFieldStatus(field).disabled)
                .map((field) => field.fieldId),
        [getFieldStatus, schemaFields],
    );

    const [selectedFields, setSelectedFields] = useState(preselectedContacts);
    const [consentChecked, setConsentChecked] = useState(false);
    const [validationError, setValidationError] = useState(null);
    const [confirmError, setConfirmError] = useState(null);
    const [confirmText, setConfirmText] = useState(null);

    const handleChange = (fieldId, checked) => {
        setValidationError(null);
        const field = schemaFields.find((f) => f.fieldId === fieldId);
        if (!field || getFieldStatus(field).disabled) return;

        const nextSelectedFields = checked ? Array.from(new Set([...selectedFields, fieldId])) : selectedFields.filter((t) => t !== fieldId);
        setSelectedFields(nextSelectedFields);
    };

    const onConsentChange = (e) => setConsentChecked(e.target.checked);

    const onConfirmTextChanged = useCallback(
        (value) => {
            setConfirmText(value);
            if (!!showConfirm) {
                setConfirmError(value === "CONFIRM" ? null : "error");
            }
        },
        [showConfirm],
    );

    const hasValidContactSelection = selectedFields.some((fieldId) => {
        const field = schemaFields.find((f) => f.fieldId === fieldId);
        if (!field || !isContactField(field)) return false;
        const status = getFieldStatus(field);
        return !!status.contactType && status.isValidContact && !status.disabled;
    });

    const onOkClick = () => {
        if (showConfirm && confirmText !== "CONFIRM") {
            setConfirmError("error");
            return;
        }
        if (!consentChecked) return;
        setConfirmError(null);

        if (!hasValidContactSelection) {
            setValidationError("Select at least one Email or Phone attribute with a valid value to send the verification link.");
            return;
        }

        onOk(selectedFields, consentChecked);
    };

    return (
        <Modal
            closable={!busy}
            open={visible}
            styles={{ body: { minHeight: "25vh" } }}
            centered
            title={`Consumer Record Verification (${recordId})`}
            okText={!busy ? "Send Magic Link" : "Sending magic link..."}
            cancelText="Cancel"
            onCancel={onCancel}
            onOk={onOkClick}
            cancelButtonProps={{ disabled: busy }}
            width={1000}
            data-testid={CypressTestIds.DATASET_VERIFICATION_MODAL}
            okButtonProps={{
                disabled: busy || !consentChecked || (!!showConfirm && confirmText !== "CONFIRM") || !hasValidContactSelection,
                loading: busy,
                "data-testid": CypressTestIds.DATASET_VERIFICATION_SUBMIT_BUTTON,
            }}
        >
            <FullHeightContainerLayout
                style={{ minHeight: "25vh" }}
                showHeader={validationError !== null}
                header={validationError && <Text type="danger">{validationError}</Text>}
                content={
                    <Card size="small" title="Choose data attribute for verification" variant="borderless" style={{ width: "100%", background: "#fafafa" }}>
                        <Alert
                            type="info"
                            showIcon
                            icon={<InfoCircleOutlined style={{ fontSize: 14 }} />}
                            title={
                                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                    <MailOutlined style={{ fontSize: 14 }} />
                                    <PhoneOutlined style={{ fontSize: 14 }} />
                                    <Text>
                                        Please select at least one attribute containing a valid <strong>Email Address</strong> or <strong>Phone Number</strong> to send the
                                        verification link
                                    </Text>
                                </div>
                            }
                            style={{ marginBottom: 16, padding: "8px 12px" }}
                        />
                        {schemaFields.map((field) => {
                            const status = getFieldStatus(field);
                            const suffix = status.contactType === "email" ? " • Email" : status.contactType === "phone" ? " • Phone" : status.disabled ? " (Unavailable)" : "";

                            return (
                                <Tooltip title={status.tooltip} key={field.fieldId}>
                                    <CheckableTag
                                        data-testid={`${CypressTestIds.DATASET_VERIFICATION_ATTRIBUTE}-${field.fieldId}`}
                                        aria-disabled={status.disabled}
                                        style={{
                                            border: "1px solid #d9d9d9",
                                            opacity: status.disabled ? 0.4 : 1,
                                            cursor: status.disabled ? "not-allowed" : "pointer",
                                            marginBottom: 8,
                                        }}
                                        className="selectable-tag"
                                        checked={selectedFields.includes(field.fieldId)}
                                        onChange={(checked) => {
                                            if (status.disabled) return;
                                            handleChange(field.fieldId, checked);
                                        }}
                                    >
                                        {field.name}
                                        {suffix}
                                    </CheckableTag>
                                </Tooltip>
                            );
                        })}
                    </Card>
                }
                showFooter
                footer={
                    <div className="flex w-full flex-col gap-4">
                        <div className="flex flex-row gap-2 rounded-md bg-slate-50 p-2">
                            <Checkbox data-testid={CypressTestIds.DATASET_VERIFICATION_CONSENT_CHECKBOX} checked={consentChecked} onChange={onConsentChange}>
                                Request Consent
                            </Checkbox>
                            {!consentChecked && <Text type="danger">(Checkbox is mandatory.)</Text>}
                        </div>
                        {showConfirm && (
                            <div className="flex flex-col gap-2">
                                <Input
                                    data-testid={CypressTestIds.DATASET_VERIFICATION_CONFIRMATION_INPUT}
                                    status={confirmError}
                                    value={confirmText}
                                    onChange={(e) => onConfirmTextChanged(e.target.value)}
                                    placeholder="Type CONFIRM to run a bulk job"
                                />
                                {!!confirmError && (
                                    <Text type="danger" data-testid={CypressTestIds.DATASET_VERIFICATION_CONFIRMATION_ERROR}>
                                        Type CONFIRM to verify to run a bulk job
                                    </Text>
                                )}
                            </div>
                        )}
                    </div>
                }
            ></FullHeightContainerLayout>
        </Modal>
    );
};

export default DataObjectRequestVerificationModal;
